<template>
    <div>
        <div class="row mt-2 align-items-center">
            <div class="col-12 col-sm-7">
                <h6 class="font-weight-bold no-wrap mb-0">Select a Canva design</h6>
            </div>
            <div class="col-12 col-sm-5">
                <modal-import-search @search-text-change="updateSearchText" />
            </div>
        </div>
        <modal-import-list class="mt-2 mb-4" :items="items" :is-loading="isLoading" @item-selected="itemSelected" />
        <b-pagination
            v-model="currentPage"
            :total-rows="totalCanvaDesigns"
            :per-page="perPage"
            aria-controls="my-table"
            align="center"
        />
    </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue';
import store from '@/store';
import ModalImportSearch from '../../search/ModalImportSearch.vue';
import ModalImportList from '../../list/ModalImportList.vue';
import { Item } from '@/components/Layers/modal/types';
import { useFileLayers } from '@/composables/externalFileParser';
import { exportCanvaDesign, getCanvaDesignPages, isAuthenticated } from '@/api/canva';
import { ImportLayersElements } from '@/typings/layers/layers';
import { getUser } from '@/api/user';
import { CanvaDesignExportRequest, CanvaExportFormat } from '@/typings/parsers/externalFileParser';

const emit = defineEmits<{
    (event: 'canva-design-selected'): void;
}>();

const { data: pptxLayers, parseAndExtractLayers } = useFileLayers('CANVA');

const maxItemsPerRequest = 25;
const perPage = 5;
const currentPage = ref(1);
const isLoading = ref(true);
const searchText = ref('');

const canvaDesigns = computed(() => store.state.storeLayersModal!.canvaDesigns);
//this is not the real lenght, its more like "all the designs this get returned", canva api paginates by a lenght of 25
const totalCanvaDesigns = computed(() => store.state.storeLayersModal!.totalCanvaDesigns);
const user = computed(() => store.state.user);
const items = computed(() => {
    const totalPages = Math.ceil(totalCanvaDesigns.value / perPage);
    if (currentPage.value < 1 || currentPage.value > totalPages) {
        return [];
    }
    const startIndex = (currentPage.value - 1) * perPage;
    const endIndex = startIndex + perPage;
    return canvaDesigns.value.designs.slice(startIndex, endIndex).map(
        (canvaDesign) =>
            ({
                thumb: canvaDesign.thumbnailUrl,
                name: canvaDesign.title,
                id: canvaDesign.id
            } as Item)
    );
});

watch(currentPage, () => {
    if (maxItemsPerRequest / perPage !== currentPage.value || !canvaDesigns.value.next) return;
    fetchCanvaDesigns(canvaDesigns.value.next);
});

onMounted(async () => {
    await fetchCanvaDesigns();
});

const updateSearchText = async (value: string) => {
    searchText.value = value;
    currentPage.value = 1;
    await fetchCanvaDesigns();
};

const fetchCanvaDesigns = async (paginationReference?: string) => {
    try {
        isLoading.value = true;
        await store.dispatch('storeLayersModal/getCanvaDesigns', {
            token: user.value?.CanvaAccessToken,
            continuation: paginationReference,
            search: searchText.value
        });
    } catch (err) {
        console.log('error', (err as Error).message);
    } finally {
        isLoading.value = false;
    }
};

const itemSelected = async (item: Item) => {
    try {
        const design = canvaDesigns.value.designs.find((design) => design.id == item.id);
        if (!design) return;
        if (!user.value) return;
        const authResponse = await isAuthenticated(user.value.Username);
        if (!authResponse.isAuthenticated) return;
        const updatedUser = await getUser();
        store.commit('user', updatedUser);
        const canvaPages = await getCanvaDesignPages(design.id, user.value.CanvaAccessToken!);
        if (!canvaPages.length) return;
        isLoading.value = true;
        const importLayers: ImportLayersElements = {
            type: 'CANVA',
            mode: 'LAYERS',
            pages: canvaPages.map((page) => ({
                fileUrl: page.thumbnailUrl,
                pageNumber: page.pageNumber,
                name: `Page ${page.pageNumber}`,
                artWorkHeight: 1000,
                artWorkWidth: 1000,
                elements: []
            })),
            artWorkThumbnail: design.thumbnailUrl,
            artworkName: design.title,
            designId: design.id,
            includeDimensions: true,
            importedFileName: item.name
        };

        const body: CanvaDesignExportRequest = {
            username: user.value.Username,
            designId: design.id,
            format: CanvaExportFormat.PPTX,
            page: 1,
            pngOptions: null
        };
        const exportResponse = await exportCanvaDesign(body);

        if (!exportResponse.succeedd) {
            return;
        }
        const response = await fetch(exportResponse.content);
        const buffer = await response.arrayBuffer();
        await parseAndExtractLayers(buffer, importLayers);
        if (!pptxLayers.value) return;
        store.dispatch('storeLayersModal/setSelectedItem', item);
        store.dispatch('storeLayersModal/setParsedFileLayers', pptxLayers.value);
        emit('canva-design-selected');
    } finally {
        isLoading.value = false;
    }
};
</script>
